import * as React from 'react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { VENDOR_SERVICES, VENDOR_SERVICE_LABELS, type VendorService } from '@lib/enums/vendor-service.enum'

interface VendorServicesPickerProps {
  /** The services currently offered. */
  value: VendorService[]
  /** Persist the new full set. Rejects surface as an inline error. */
  onSave: (services: VendorService[]) => Promise<void>
  busy?: boolean
}

function sameSet(a: VendorService[], b: VendorService[]): boolean {
  if (a.length !== b.length) return false
  const set = new Set(a)
  return b.every((s) => set.has(s))
}

/**
 * Choose which services a vendor offers — a checklist of every service with an
 * explicit Save. Un-offering a service hides its config (never deletes it), so
 * this is a safe, reversible toggle.
 */
export function VendorServicesPicker({ value, onSave, busy }: VendorServicesPickerProps) {
  const [draft, setDraft] = React.useState<VendorService[]>(value)
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  // Re-seed if the persisted value changes (e.g. after a save elsewhere).
  const seededFrom = React.useRef(value)
  React.useEffect(() => {
    if (!sameSet(seededFrom.current, value)) {
      seededFrom.current = value
      setDraft(value)
    }
  }, [value])

  const selected = React.useMemo(() => new Set(draft), [draft])
  const dirty = !sameSet(draft, value)
  /** Stopping furniture hides the vendor's pieces from every venue it serves — asked once. */
  const [confirmingFurniture, setConfirmingFurniture] = React.useState(false)
  const dropsFurniture = value.includes('furniture') && !selected.has('furniture')

  function toggle(service: VendorService): void {
    setSaved(false)
    setDraft((prev) => (prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]))
  }

  async function save(): Promise<void> {
    if (dropsFurniture && !confirmingFurniture) {
      setConfirmingFurniture(true)
      return
    }
    setConfirmingFurniture(false)
    setError(null)
    setSaved(false)
    try {
      await onSave(VENDOR_SERVICES.filter((s) => selected.has(s)))
      setSaved(true)
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  return (
    <div className="space-y-4">
      <ul className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {VENDOR_SERVICES.map((service) => (
          <li key={service}>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={selected.has(service)} disabled={busy} onChange={() => toggle(service)} />
              {VENDOR_SERVICE_LABELS[service]}
            </label>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-3">
        <Button pending={busy} type="button" onClick={() => void save()} disabled={!dirty}>
          <Check className="h-4 w-4" />
          {busy ? 'Saving…' : 'Save services'}
        </Button>
        {error ? <FormError>{error}</FormError> : null}
        {saved && !error ? <p className="text-sm text-ok-ink">Saved.</p> : null}
      </div>
      <ConfirmDialog
        open={confirmingFurniture}
        onOpenChange={setConfirmingFurniture}
        title="Stop offering furniture?"
        description="Your pieces stop showing in the furniture galleries of every venue you serve, so nothing new can be rented. Events that already rent them keep their rentals, and your library is kept for when you offer furniture again."
        confirmLabel="Stop offering furniture"
        destructive
        busy={busy}
        onConfirm={() => void save()}
      />
    </div>
  )
}
